import { getAuthToken, appCheckInstance, signOut } from './firebase';
import { getToken } from 'firebase/app-check';
import { env } from './env';
import { ErrorCodes } from '../constants/errorCodes';
import { AuthError } from './errors';

const baseUrl = '/api';

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  authenticated?: boolean;
  isFormData?: boolean;
  responseType?: 'json' | 'blob';
  returnFilename?: boolean;
}

// Concurrent requests share a single in-flight token fetch instead of each
// awaiting their own. On a cold mobile network after idle this collapses N
// serialized awaits into one round trip.
let inflightAuth: Promise<string | undefined> | null = null;
let inflightAppCheck: Promise<string | null> | null = null;

async function cachedAuthToken(): Promise<string | undefined> {
  if (!inflightAuth) {
    inflightAuth = getAuthToken().finally(() => {
      inflightAuth = null;
    });
  }
  return inflightAuth;
}

async function getAppCheckToken(forceRefresh = false): Promise<string | null> {
  // Skip App Check in development
  if (env.VITE_NODE_ENV === 'development') {
    return null;
  }

  // Skip if App Check is not initialized (e.g., missing site key)
  if (!appCheckInstance) {
    return null;
  }

  // Force refresh always bypasses the in-flight cache so 403-retry gets a
  // genuinely fresh token.
  if (forceRefresh) {
    const r = await getToken(appCheckInstance, true);
    return r.token;
  }

  if (!inflightAppCheck) {
    inflightAppCheck = getToken(appCheckInstance, false)
      .then((r) => r.token)
      .finally(() => {
        inflightAppCheck = null;
      });
  }
  return inflightAppCheck;
}

const isAppCheckError = (message: string) =>
  message.toLowerCase().includes('app check') ||
  message.toLowerCase().includes('appcheck') ||
  message.toLowerCase().includes('x-firebase-appcheck');

const buildFetchArgs = (
  endpoint: string,
  fetchOptions: Omit<RequestOptions, 'authenticated'>,
  headers: Record<string, string>,
): [string, RequestInit] => [
  `${baseUrl}${endpoint}`,
  {
    method: fetchOptions.method || 'GET',
    headers,
    body: fetchOptions.isFormData
      ? (fetchOptions.body as FormData)
      : fetchOptions.body
      ? JSON.stringify(fetchOptions.body)
      : undefined,
  },
];

export const request = async <T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> => {
  const { authenticated = true, ...fetchOptions } = options;

  const appCheckToken = await getAppCheckToken();

  const headers: Record<string, string> = {
    ...fetchOptions.headers,
  };

  if (authenticated) {
    const token = await cachedAuthToken();
    if (!token) {
      await signOut();
      // Only navigate if not already on login page to preserve the original redirect-url
      if (window.location.pathname !== '/login') {
        const redirectUrl = encodeURIComponent(
          `${window.location.pathname}${window.location.search}`,
        );
        window.location.href = `/login?redirect-url=${redirectUrl}`;
      }
      throw new AuthError('User is not authorized');
    }
    headers.Authorization = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData - browser will set it with boundary
  if (!options.isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  // Add App Check token if available
  if (appCheckToken) {
    headers['X-Firebase-AppCheck'] = appCheckToken;
  }

  let response = await fetch(
    ...buildFetchArgs(endpoint, fetchOptions, headers),
  );

  // Retry once with a force-refreshed App Check token if the request failed due to an invalid/expired token
  if (response.status === 403) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message ?? '';

    if (isAppCheckError(errorMessage)) {
      const freshToken = await getAppCheckToken(true);
      if (freshToken) {
        headers['X-Firebase-AppCheck'] = freshToken;
        response = await fetch(
          ...buildFetchArgs(endpoint, fetchOptions, headers),
        );
      }
    } else {
      // Non-App Check 403, handle normally
      const errorCode = errorData.errorCode;
      if (
        errorCode === ErrorCodes.USER_DEACTIVATED ||
        errorCode === ErrorCodes.ACCOUNT_INACTIVE
      ) {
        await signOut();
        throw new AuthError(errorMessage);
      }
      throw new Error(errorMessage || `HTTP 403: ${response.statusText}`);
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.message || `HTTP ${response.status}: ${response.statusText}`;
    const errorCode = errorData.errorCode;

    // Handle user deactivation and account inactive errors
    if (
      errorCode === ErrorCodes.USER_DEACTIVATED ||
      errorCode === ErrorCodes.ACCOUNT_INACTIVE
    ) {
      await signOut();
      throw new AuthError(errorMessage);
    }

    const err = new Error(errorMessage) as Error & { errors?: string[] };
    if (Array.isArray(errorData.errors)) {
      err.errors = errorData.errors;
    }
    throw err;
  }

  // Handle blob responses
  if (options.responseType === 'blob') {
    const blob = await response.blob();
    if (options.returnFilename) {
      const contentDisposition = response.headers.get('content-disposition');
      const match = contentDisposition?.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : null;
      return { blob, filename } as T;
    }
    return blob as T;
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    return {} as T;
  }

  return response.json();
};

export const unauthenticatedRequest = async <T>(
  endpoint: string,
  options: Omit<RequestOptions, 'authenticated'> = {},
): Promise<T> => {
  return request<T>(endpoint, { ...options, authenticated: false });
};
